/**
 * Obstructions Slice
 *
 * Data + UI slice for the obstructions artifact. Implements the
 * `ArtifactSlice` contract so the artifact registry can drive overlay
 * open/close, form conflict guard, and view-mode sync uniformly.
 *
 * Mirrors `notesSlice.ts` in shape — the per-artifact panel helpers
 * (`applyOpenDetailsPanel`, `applyCloseDetails`, `applyOpenFormPanel`,
 * `applyCloseFormPanel`, `handleSelectItem`) come from
 * `@/artifacts/panelHelpers` to keep the cross-artifact behaviour
 * identical (single side-panel rule, mobile minimization, selection
 * id normalisation).
 */
import { obstructionsService } from '@/services/api';
import { isGuid } from '@/utils/idHelpers';
import type { ApiError } from '@/types/api/errors';
import type { ObstructionFiltersParams } from '@/types/api/requests';
import type {
  Obstruction,
  ObstructionListItem,
  ObstructionMapItem,
  CreateObstructionData,
  UpdateObstructionData,
} from '@/types/obstructions';
import { ObstructionStatus } from '@/types/obstructions';
import type { Coordinates } from '@/types';
import {
  mapObstructionFromApi,
  mapObstructionListItemFromApi,
  mapObstructionMapItemFromApi,
  obstructionToListItem,
} from '../mappers/obstructionMapper';
import {
  applyOpenDetailsPanel,
  applyCloseDetails,
  applyOpenFormPanel,
  applyCloseFormPanel,
  applyClearStaleSelection,
  handleSelectItem,
  closeCompetingArtifacts,
  withShareFormGuard,
  type PanelDraftState,
} from '@/artifacts/panelHelpers';
import {
  buildObstructionFiltersParams,
  type ObstructionFiltersSlice,
} from './obstructionFiltersSlice';

// ===== SLICE INTERFACE =====

export interface ObstructionsSlice {
  obstructions: {
    items: ObstructionListItem[];
    mapItems: ObstructionMapItem[];
    selectedId: string | null;
    detailedObstruction: Obstruction | null;
    isLoading: boolean;
    hasLoaded: boolean;
    isSyncing: boolean;
    error: ApiError | null;
    lastSyncAt: Date | null;

    // Pagination
    hasNextPage: boolean;
    totalCount: number;
    currentPage: number;
    _lastFetchFilters: ObstructionFiltersParams | undefined;

    // ===== ArtifactSlice contract =====
    /** Whether the obstruction details panel is open. */
    detailsIsOpen: boolean;
    /** Whether the obstruction form is open (create/edit). */
    formIsOpen: boolean;
    /** Form mode discriminator. */
    formMode: 'create' | 'edit' | null;
    /** Id of the obstruction currently being edited (null in create mode). */
    formEditingId: string | null;
    /** Anchor point for the form. */
    formAnchorPoint: Coordinates | null;
    /** Whether the form has unsaved user input (synced from ObstructionFormWizard). */
    formIsDirty: boolean;

    /** Open the obstruction details panel. */
    openDetails: () => void;
    /** Close the obstruction details panel. */
    closeDetails: () => void;
    /**
     * Open the obstruction form. Routes through `formConflict.request` so a
     * dirty competing form prompts before discard.
     */
    openForm: (mode: 'create' | 'edit', itemId?: string) => void;
    /** Close the obstruction form and reset cross-cutting flags. */
    closeForm: () => void;
    /** Slice-specific helper: set the form anchor point without opening/closing the form. */
    setFormAnchorPoint: (point: Coordinates | null) => void;
    /**
     * Create a new obstruction at the given coordinates: set the anchor point,
     * zoom the map to it, open the create form, and exit obstruction
     * location-selection mode. Mirrors the task slice's create-at flow.
     */
    createObstructionAt: (coords: Coordinates) => void;
    /** Update the dirty flag from the active form component. */
    setFormIsDirty: (value: boolean) => void;
    /** ArtifactSlice contract: thin alias over `selectObstruction`. */
    selectItem: (id: string) => void;

    // CRUD actions
    fetchObstructions: () => Promise<void>;
    loadMore: () => Promise<void>;
    fetchForMap: () => Promise<void>;
    /** Replaces mapItems wholesale — used by public share hydration. */
    setMapItems: (items: ObstructionMapItem[]) => void;
    fetchObstructionByShortId: (id: string) => Promise<void>;
    createObstruction: (data: CreateObstructionData) => Promise<string | null>;
    updateObstruction: (
      id: string,
      data: UpdateObstructionData
    ) => Promise<boolean>;
    deleteObstruction: (id: string) => Promise<boolean>;
    updateStatus: (
      id: string,
      status: ObstructionStatus,
      reason?: string
    ) => Promise<boolean>;
    selectObstruction: (id: string | null) => void;
    setStatus: (status: ObstructionStatus | null) => void;
    fetchStats: () => Promise<void>;

    // State helpers
    setLoading: (loading: boolean) => void;
    setError: (error: ApiError | null) => void;
    clearError: () => void;
  };
}

// ===== CROSS-SLICE STATE TYPE =====

interface StoreWithObstructionFilters {
  obstructions: ObstructionsSlice['obstructions'];
  obstructionFilters?: ObstructionFiltersSlice['obstructionFilters'];
  // Cross-slice dependencies needed by ArtifactSlice actions
  tasks: {
    formIsOpen: boolean;
    detailsIsOpen: boolean;
    closeDetails: () => void;
    closeForm: () => void;
  };
  notes: {
    formIsOpen: boolean;
    detailsIsOpen: boolean;
    closeDetails: () => void;
    closeForm: () => void;
  };
  shareForm: { formIsOpen: boolean };
  formConflict: { request: (onProceed: () => void) => void };
  map: { zoomTo: (coords: Coordinates | null) => void };
  ui: {
    cancelTaskLocationSelection: () => void;
    cancelNoteLocationSelection: () => void;
    cancelObstructionLocationSelection: () => void;
  };
  setHideOtherMarkers: (value: boolean) => void;
}

// ===== SLICE CREATOR =====
//
// Like `notesSlice`, the `set` parameter is widened so the ArtifactSlice
// actions can mutate cross-slice flags (e.g. panel state, hover preview)
// inside a single draft transaction.
type ObstructionsSliceSetState = ObstructionsSlice & PanelDraftState;

export const createObstructionsSlice = (
  set: (fn: (state: ObstructionsSliceSetState) => void) => void,
  get: () => StoreWithObstructionFilters,
  _store: unknown
) => ({
  obstructions: {
    items: [] as ObstructionListItem[],
    mapItems: [] as ObstructionMapItem[],
    selectedId: null as string | null,
    detailedObstruction: null as Obstruction | null,
    isLoading: false,
    hasLoaded: false,
    isSyncing: false,
    error: null as ApiError | null,
    lastSyncAt: null as Date | null,

    // Pagination
    hasNextPage: false,
    totalCount: 0,
    currentPage: 1,
    _lastFetchFilters: undefined as ObstructionFiltersParams | undefined,

    // ===== ArtifactSlice contract =====
    detailsIsOpen: false,
    formIsOpen: false,
    formMode: null as 'create' | 'edit' | null,
    formEditingId: null as string | null,
    formAnchorPoint: null as Coordinates | null,
    formIsDirty: false,

    // ===== ArtifactSlice actions =====

    openDetails: () => {
      // Single right-panel rule: guard-close the share config form (prompts
      // when dirty) before revealing the details panel.
      withShareFormGuard(get(), () => {
        const store = get();

        // Cross-artifact mutual exclusion: close competing artifacts' panels.
        closeCompetingArtifacts([store.tasks, store.notes]);
        store.ui.cancelTaskLocationSelection();
        store.ui.cancelNoteLocationSelection();
        store.ui.cancelObstructionLocationSelection();

        set((state) => {
          state.obstructions.formIsOpen = false;
          state.obstructions.formMode = null;
          state.obstructions.detailsIsOpen = true;
          applyOpenDetailsPanel(state);
        });
      });
    },

    closeDetails: () => {
      set((state) => applyCloseDetails(state, state.obstructions));
    },

    openForm: (mode: 'create' | 'edit', itemId?: string) => {
      // Idempotent re-open: re-syncing the already-open form (e.g. the panel
      // remount on a map ↔ list/kanban view switch) is a no-op. It must not
      // hit the unsaved-changes guard (which would falsely prompt) nor rewrite
      // formMode (which would remount the panel and reset its size/fullscreen).
      const current = get().obstructions;
      if (
        current.formIsOpen &&
        current.formMode === mode &&
        current.formEditingId === (mode === 'edit' ? (itemId ?? null) : null)
      ) {
        return;
      }

      // Route through the universal unsaved-changes guard: a dirty competing
      // form prompts before discard; clean forms close silently first.
      get().formConflict.request(() => {
        const store = get();

        // Cross-artifact mutual exclusion
        closeCompetingArtifacts([store.tasks, store.notes]);
        store.ui.cancelTaskLocationSelection();
        store.ui.cancelNoteLocationSelection();
        store.ui.cancelObstructionLocationSelection();

        set((state) => {
          applyOpenFormPanel(state, { minimizeOnMobile: false });
          state.obstructions.formIsOpen = true;
          state.obstructions.formMode = mode;
          state.obstructions.formEditingId =
            mode === 'edit' ? (itemId ?? null) : null;
          state.obstructions.formIsDirty = false;
          // Mutual exclusion within obstructions: close details when opening form
          state.obstructions.detailsIsOpen = false;
        });

        // Auto-hide other markers when opening obstruction form
        store.setHideOtherMarkers(true);
      });
    },

    closeForm: () => {
      if (!get().obstructions.formIsOpen) return;

      const store = get();

      set((state) => {
        state.obstructions.formIsOpen = false;
        state.obstructions.formMode = null;
        state.obstructions.formEditingId = null;
        state.obstructions.formAnchorPoint = null;
        state.obstructions.formIsDirty = false;
        applyCloseFormPanel(state);
      });

      // Restore artifact visibility when the obstruction form closes
      store.setHideOtherMarkers(false);
    },

    setFormAnchorPoint: (point: Coordinates | null) => {
      const current = get().obstructions.formAnchorPoint;
      if (point?.lat === current?.lat && point?.lng === current?.lng) return;
      set((state) => {
        state.obstructions.formAnchorPoint = point;
      });
    },

    createObstructionAt: (coords: Coordinates) => {
      const store = get();
      store.obstructions.setFormAnchorPoint(coords);
      store.map.zoomTo(coords);
      store.obstructions.openForm('create');
      store.ui.cancelObstructionLocationSelection();
    },

    setFormIsDirty: (value: boolean) => {
      if (get().obstructions.formIsDirty === value) return;
      set((state) => {
        state.obstructions.formIsDirty = value;
      });
    },

    // ===== CRUD ACTIONS =====

    fetchObstructions: async () => {
      set((state) => {
        state.obstructions.isLoading = true;
        state.obstructions.error = null;
      });

      try {
        const storeState = get();
        const filterParams = buildObstructionFilterParamsFromState(storeState);
        filterParams.page = 1;

        // Save filter snapshot for loadMore consistency
        set((state) => {
          state.obstructions._lastFetchFilters = filterParams;
        });

        const response = await obstructionsService.getAll(filterParams);

        set((state) => {
          state.obstructions.items = response.data.items.map(
            mapObstructionListItemFromApi
          );
          state.obstructions.currentPage = 1;
          state.obstructions.hasNextPage =
            response.data.page < response.data.totalPages;
          state.obstructions.totalCount = response.data.totalCount;
          state.obstructions.isLoading = false;
          state.obstructions.hasLoaded = true;
          state.obstructions.lastSyncAt = new Date();
          applyClearStaleSelection(state.obstructions, () => {
            state.obstructions.detailedObstruction = null;
          });
        });
      } catch (error) {
        set((state) => {
          state.obstructions.isLoading = false;
          state.obstructions.hasLoaded = true;
          state.obstructions.error = error as ApiError;
        });
      }
    },

    loadMore: async () => {
      const storeState = get();
      if (
        !storeState.obstructions.hasNextPage ||
        storeState.obstructions.isLoading
      ) {
        return;
      }

      set((state) => {
        state.obstructions.isLoading = true;
      });

      const nextPage = storeState.obstructions.currentPage + 1;
      // Use saved filter snapshot — NOT live filter state (prevents race conditions)
      const params = {
        ...storeState.obstructions._lastFetchFilters,
        page: nextPage,
      };

      try {
        const response = await obstructionsService.getAll(params);
        const newItems = response.data.items.map(mapObstructionListItemFromApi);

        set((state) => {
          state.obstructions.items.push(...newItems);
          state.obstructions.currentPage = nextPage;
          state.obstructions.hasNextPage =
            response.data.page < response.data.totalPages;
          state.obstructions.totalCount = response.data.totalCount;
          state.obstructions.isLoading = false;
          state.obstructions.lastSyncAt = new Date();
        });
      } catch (error) {
        set((state) => {
          state.obstructions.isLoading = false;
          state.obstructions.error = error as ApiError;
        });
      }
    },

    fetchForMap: async () => {
      try {
        const response = await obstructionsService.getForMap({});

        set((state) => {
          state.obstructions.mapItems = response.data.map(
            mapObstructionMapItemFromApi
          );
        });
      } catch (error) {
        console.error('[obstructionsSlice] fetchForMap failed:', error);
      }
    },

    setMapItems: (items: ObstructionMapItem[]) => {
      set((state) => {
        state.obstructions.mapItems = items;
      });
    },

    fetchObstructionByShortId: async (id: string) => {
      set((state) => {
        state.obstructions.isSyncing = true;
      });

      try {
        const response = isGuid(id)
          ? await obstructionsService.getById(id)
          : await obstructionsService.getByShortId(id);
        const mapped = mapObstructionFromApi(response.data);

        // Discard stale responses: if the user navigated away and
        // selectedId no longer corresponds to this request, drop the result.
        const currentSelectedId = get().obstructions.selectedId;
        if (
          currentSelectedId !== null &&
          currentSelectedId !== mapped.id &&
          currentSelectedId !== mapped.shortId &&
          currentSelectedId !== id
        ) {
          set((state) => {
            state.obstructions.isSyncing = false;
          });
          return;
        }

        set((state) => {
          const existingIndex = state.obstructions.items.findIndex(
            (item) => item.id === mapped.id
          );
          if (existingIndex >= 0) {
            state.obstructions.items[existingIndex] =
              obstructionToListItem(mapped);
          }
          state.obstructions.detailedObstruction = mapped;

          // Upgrade shortId → GUID in selectedId and formEditingId.
          if (
            state.obstructions.selectedId === mapped.shortId ||
            state.obstructions.selectedId === id
          ) {
            state.obstructions.selectedId = mapped.id;
          }

          if (
            state.obstructions.formEditingId &&
            state.obstructions.formEditingId !== mapped.id &&
            state.obstructions.formEditingId === mapped.shortId
          ) {
            state.obstructions.formEditingId = mapped.id;
          }

          state.obstructions.isSyncing = false;
        });
      } catch (error) {
        set((state) => {
          state.obstructions.isSyncing = false;
          state.obstructions.error = error as ApiError;
        });
      }
    },

    /**
     * Create an obstruction.
     *
     * Accepts a `CreateObstructionData` DTO (Wave 4) and maps it to the API
     * `CreateObstructionRequest` JSON payload. Dates are serialised to ISO
     * strings; permit dates are already `YYYY-MM-DD` strings from the form.
     */
    createObstruction: async (data: CreateObstructionData) => {
      set((state) => {
        state.obstructions.isSyncing = true;
        state.obstructions.error = null;
      });

      try {
        const response = await obstructionsService.create({
          title: data.title,
          description: data.description,
          type: data.type,
          severity: data.severity,
          impact: data.impact,
          location: data.location,
          internalNotes: data.internalNotes,
          plannedStartDate: data.plannedStartDate.toISOString(),
          plannedEndDate: data.plannedEndDate.toISOString(),
          contractor: {
            company: data.contractor.company,
            manager: data.contractor.manager,
            phone: data.contractor.phone,
            email: data.contractor.email,
            emergencyContact: data.contractor.emergencyContact,
            permitNumber: data.contractor.permitNumber,
            permitIssuedDate: data.contractor.permitIssuedDate,
            permitExpiryDate: data.contractor.permitExpiryDate,
            notes: data.contractor.notes,
          },
          anchorPoint: {
            lat: data.anchorPoint.lat,
            lng: data.anchorPoint.lng,
          },
          affectedRoads: data.affectedRoads.map((affectedRoad) => ({
            location: affectedRoad.location,
            description: affectedRoad.description,
          })),
        });

        const mapped = mapObstructionFromApi(response.data);
        const listItem = obstructionToListItem(mapped);

        set((state) => {
          state.obstructions.items.unshift(listItem);
          state.obstructions.totalCount += 1;
          state.obstructions.selectedId = mapped.id;
          state.obstructions.isSyncing = false;
        });

        // Refresh map markers so the new obstruction appears on the map
        get().obstructions.fetchForMap();

        return mapped.id;
      } catch (error) {
        set((state) => {
          state.obstructions.isSyncing = false;
          state.obstructions.error = error as ApiError;
        });
        return null;
      }
    },

    updateObstruction: async (id: string, data: UpdateObstructionData) => {
      set((state) => {
        state.obstructions.isSyncing = true;
      });

      try {
        const response = await obstructionsService.update(id, {
          title: data.title,
          description: data.description,
          type: data.type,
          severity: data.severity,
          impact: data.impact,
          location: data.location,
          internalNotes: data.internalNotes,
          plannedStartDate: data.plannedStartDate?.toISOString(),
          plannedEndDate: data.plannedEndDate?.toISOString(),
          contractor: data.contractor
            ? {
                company: data.contractor.company,
                manager: data.contractor.manager,
                phone: data.contractor.phone,
                email: data.contractor.email,
                emergencyContact: data.contractor.emergencyContact,
                permitNumber: data.contractor.permitNumber,
                permitIssuedDate: data.contractor.permitIssuedDate,
                permitExpiryDate: data.contractor.permitExpiryDate,
                notes: data.contractor.notes,
              }
            : undefined,
          anchorPoint: data.anchorPoint
            ? { lat: data.anchorPoint.lat, lng: data.anchorPoint.lng }
            : undefined,
          affectedRoads: data.affectedRoads
            ? data.affectedRoads.map((affectedRoad) => ({
                location: affectedRoad.location,
                description: affectedRoad.description,
              }))
            : undefined,
        });

        const mapped = mapObstructionFromApi(response.data.obstruction);

        set((state) => {
          const existingIndex = state.obstructions.items.findIndex(
            (item) => item.id === mapped.id
          );
          if (existingIndex >= 0) {
            state.obstructions.items[existingIndex] =
              obstructionToListItem(mapped);
          }
          if (state.obstructions.detailedObstruction?.id === mapped.id) {
            state.obstructions.detailedObstruction = mapped;
          }
          state.obstructions.isSyncing = false;
        });

        // Refresh map markers (location/status may have changed)
        get().obstructions.fetchForMap();

        return true;
      } catch (error) {
        set((state) => {
          state.obstructions.isSyncing = false;
          state.obstructions.error = error as ApiError;
        });
        return false;
      }
    },

    deleteObstruction: async (id: string) => {
      set((state) => {
        state.obstructions.isSyncing = true;
      });

      try {
        await obstructionsService.delete(id);

        set((state) => {
          state.obstructions.items = state.obstructions.items.filter(
            (item) => item.id !== id
          );
          state.obstructions.mapItems = state.obstructions.mapItems.filter(
            (mapItem) => mapItem.id !== id
          );
          if (state.obstructions.selectedId === id) {
            state.obstructions.selectedId = null;
          }
          if (state.obstructions.detailedObstruction?.id === id) {
            state.obstructions.detailedObstruction = null;
          }
          state.obstructions.totalCount = Math.max(
            0,
            state.obstructions.totalCount - 1
          );
          state.obstructions.isSyncing = false;
        });

        return true;
      } catch (error) {
        set((state) => {
          state.obstructions.isSyncing = false;
          state.obstructions.error = error as ApiError;
        });
        return false;
      }
    },

    updateStatus: async (
      id: string,
      status: ObstructionStatus,
      reason?: string
    ) => {
      set((state) => {
        state.obstructions.isSyncing = true;
      });

      try {
        const response = await obstructionsService.updateStatus(id, {
          status,
          reason: reason ?? null,
        });
        const mapped = mapObstructionFromApi(response.data.obstruction);

        set((state) => {
          const existingIndex = state.obstructions.items.findIndex(
            (item) => item.id === mapped.id
          );
          if (existingIndex >= 0) {
            state.obstructions.items[existingIndex] =
              obstructionToListItem(mapped);
          }
          if (state.obstructions.detailedObstruction?.id === mapped.id) {
            state.obstructions.detailedObstruction = mapped;
          }
          state.obstructions.isSyncing = false;
        });

        get().obstructions.fetchForMap();
        return true;
      } catch (error) {
        set((state) => {
          state.obstructions.isSyncing = false;
          state.obstructions.error = error as ApiError;
        });
        return false;
      }
    },

    selectObstruction: (id: string | null) => {
      handleSelectItem(
        set,
        id,
        (draft, val) => {
          draft.obstructions.selectedId = val;
        },
        get().obstructions.detailedObstruction,
        (fetchId) => get().obstructions.fetchObstructionByShortId(fetchId),
        (draft) => {
          draft.obstructions.detailedObstruction = null;
        }
      );
    },

    selectItem: (id: string): void => {
      get().obstructions.selectObstruction(id);
    },

    /**
     * Filter helper: replace the active `statuses` filter with a single-value
     * array (or clear it). Convenience action for status tab UIs.
     */
    setStatus: (status: ObstructionStatus | null) => {
      const filtersSlice = get().obstructionFilters;
      if (!filtersSlice) return;
      filtersSlice.setStatuses(status === null ? [] : [status]);
    },

    fetchStats: async () => {
      try {
        await obstructionsService.getStats();
        // Stats are surfaced via dedicated panels in later waves — Wave 1
        // only wires the service call so consumers can verify the contract.
      } catch (error) {
        set((state) => {
          state.obstructions.error = error as ApiError;
        });
      }
    },

    // ===== STATE HELPERS =====

    setLoading: (loading: boolean) => {
      set((state) => {
        state.obstructions.isLoading = loading;
      });
    },

    setError: (error: ApiError | null) => {
      set((state) => {
        state.obstructions.error = error;
      });
    },

    clearError: () => {
      set((state) => {
        state.obstructions.error = null;
      });
    },
  },
});

// ===== INTERNAL HELPERS =====

/**
 * Read the live obstruction filter slice (if mounted) and project it to
 * the API params shape. Decoupled from the public `buildObstructionFiltersParams`
 * exporter to keep that helper a pure function on the filter state only.
 */
function buildObstructionFilterParamsFromState(
  storeState: StoreWithObstructionFilters
): ObstructionFiltersParams {
  const filters = storeState.obstructionFilters;
  if (!filters) return {};
  return buildObstructionFiltersParams(filters);
}
